import { motion, useReducedMotion } from "framer-motion";
import GalleryTile from "./GalleryTile";

const stepVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

const mediaVariants = {
  hidden: { opacity: 0, scale: 0.975 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.08 } },
};

export default function KitBoatTimeline({ items }) {
  const reduceMotion = useReducedMotion();

  return (
    <div data-testid="kit-build-process" className="relative">
      <div className="mb-12 max-w-2xl">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#007bff]">Kit build sequence</p>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-black uppercase tracking-tighter text-[#003366]">
          From flat plate to finished hull.
        </h2>
        <p className="mt-4 text-base md:text-lg leading-relaxed text-slate-600">
          Follow the kit build in order — each stage fitted, checked and welded into the next.
        </p>
      </div>

      <div className="relative">
        <div aria-hidden className="absolute left-[15px] top-0 bottom-0 w-px bg-[#007bff]/25 md:left-1/2" />
        <ol className="relative space-y-12 md:space-y-20">
          {items.map((item, index) => {
            const step = String(index + 1).padStart(2, "0");
            const reverse = index % 2 === 1;
            return (
              <motion.li
                key={item.src}
                data-testid={`kit-build-step-${step}`}
                initial={reduceMotion ? false : "hidden"}
                whileInView="visible"
                viewport={{ once: true, amount: 0.28 }}
                variants={stepVariants}
                className="relative pl-12 md:pl-0"
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-8 z-10 flex h-8 w-8 items-center justify-center border border-[#007bff]/50 bg-white font-mono text-[10px] font-bold text-[#003366] shadow-lg shadow-[#003366]/10 md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2"
                >
                  {step}
                </span>
                <div className="grid items-center gap-5 md:grid-cols-2 md:gap-16 lg:gap-20">
                  <motion.div
                    variants={mediaVariants}
                    data-testid={`kit-build-step-media-${step}`}
                    className={reverse ? "md:order-2" : ""}
                  >
                    <GalleryTile item={item} priority={index < 4} aspect="aspect-[4/3]" />
                  </motion.div>
                  <div
                    data-testid={`kit-build-step-copy-${step}`}
                    className={`relative ${reverse ? "md:order-1 md:text-right" : ""}`}
                  >
                    <span aria-hidden className="mb-4 block h-[3px] w-14 bg-[#007bff] md:mb-5" />
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#007bff]">
                      Stage {step}
                    </p>
                    <h3 className="mt-2 font-display text-2xl md:text-3xl font-black uppercase tracking-tight text-[#003366]">
                      {item.caption}
                    </h3>
                    {item.detail && <p className="mt-3 text-sm md:text-base leading-relaxed text-slate-600">{item.detail}</p>}
                  </div>
                </div>
              </motion.li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
